/* eslint-disable max-lines */
import {
  useAIRecommendation,
  useAlarmMedia,
  useAlarms,
  useTenantDetails,
} from '@hakimo-ui/hakimo/data-access';
import {
  SOP,
  toUnauthorizedFaces,
  UnauthorizedFaceDetection,
  UnauthorizedFaceSeekDetection,
} from '@hakimo-ui/hakimo/feature-shared';
import {
  DataVersion,
  LocationAlarm,
  StatusType,
} from '@hakimo-ui/hakimo/types';
import {
  isUnifiedDataVersion,
  toast,
  useLocalStorage,
  withAuthz,
} from '@hakimo-ui/hakimo/util';
import { HakimoSpinner } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { viewModeAtom } from '../state';
import { AIRecommendationWrapper } from './ai-recommendation/AIRecommendationWrapper';
import AlarmFrames from '../../alarm-details/alarm-media/alarm-frames/AlarmFrames';
import AlarmHeader from './alarm-header/AlarmHeader';
import AlarmUpdates from './alarm-updates/AlarmUpdates';
import AlarmVideo, { AlarmVideoHandle } from './alarm-video/AlarmVideo';
import EventTimeline from './event-timeline/EventTimeline';
import { TimelineSource } from './event-timeline/types';
import Header from './header/Header';
import LiveView from './live-view/LiveView';
import { LocationContactsWrapper } from './location-contacts/LocationContactsWrapper';
import LocationFloorplan from './location-floorplan/LocationFloorplan';
import {
  createOrUpdateTimelineSource,
  useTimelineSources,
} from './useTimelineSources';
import { useTrackAlarmEvents } from './useTrackAlarmEvents';
import { arePropsEqual, eventsDomain, getAlarmsSearchParams } from './util';

export interface Props {
  alarm: LocationAlarm;
  onStatusChange?: (status: StatusType) => void;
  sharedToken?: string;
  showSOP?: boolean;
  showContacts?: boolean;
  showResolveAction?: boolean;
  layout?: 'list' | 'details';
  // Supplied by the caller from the URL (?version=v3), never read from
  // dataVersionOverrideAtom here — this tree renders on the public shared-alarm
  // route, where the Location Alarms toggle has no meaning.
  dataVersion?: DataVersion;
}
export function AlarmOverview(props: Props) {
  const {
    alarm: locationAlarm,
    onStatusChange,
    sharedToken,
    showSOP = true,
    showContacts = true,
    showResolveAction = true,
    layout = 'list',
    dataVersion,
  } = props;
  const { data: tenantData } = useTenantDetails(
    locationAlarm.location.tenant_id ?? ''
  );
  const disableMediaDownload =
    Boolean(sharedToken) ||
    (tenantData ? tenantData.config?.disableMediaDownload ?? false : true);
  const sortedRawAlarms = useMemo(
    () =>
      locationAlarm.rawAlarms?.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      ) || [],
    [locationAlarm]
  );
  const { data: aiData } = useAIRecommendation(locationAlarm.id);
  const aiOperatorRep = aiData?.[0];

  const { onTrackResolve, onTrackTalkdown } = useTrackAlarmEvents(
    locationAlarm.id
  );

  const toastShownRef = useRef(false);
  useEffect(() => {
    if (sortedRawAlarms.length === 0 && !toastShownRef.current) {
      toast('No raw alarms found for this location alarm', { type: 'warning' });
      toastShownRef.current = true;
    }
  }, [sortedRawAlarms]);

  const initialAlarm = sharedToken
    ? sortedRawAlarms[0]
    : sortedRawAlarms[sortedRawAlarms.length - 1];
  const [selectedEvent, setSelectedEvent] = useState<{
    sourceId: string;
    eventId: string;
    timestamp: number;
  }>(
    initialAlarm
      ? {
          sourceId: initialAlarm.sourceEntity.id,
          eventId: initialAlarm.id,
          timestamp: new Date(initialAlarm.timestamp).getTime(),
        }
      : { sourceId: '', eventId: '', timestamp: 0 }
  );
  const [selectedCameraId, setSelectedCameraId] = useState<string | undefined>(
    initialAlarm?.sourceEntity?.id
  );
  const [allRawAlarmTimelineSources, setAllRawAlarmTimelineSources] = useState<
    TimelineSource[]
  >([]);
  const [isAutoplayEnabled, setIsAutoplayEnabled] = useLocalStorage<boolean>(
    'alarmsAutoplayEnabled',
    true
  );
  const [viewMode, setViewMode] = useAtom(viewModeAtom);
  const isShared = !!sharedToken;
  const { data } = useAlarmMedia({
    id: selectedEvent.eventId,
    timestamp: selectedEvent.timestamp,
    boxes: true,
    sharedToken,
    isSharedLocationAlarm: isShared,
    // Without this the media read falls back to v2 while the rest of the view
    // is on v3, so a v3 alarm renders no frames and no detection boxes.
    dataVersion,
  });
  const selectedRawAlarm = useMemo(
    () => sortedRawAlarms.find((a) => a.id === selectedEvent.eventId),
    [sortedRawAlarms, selectedEvent.eventId]
  );
  const isUnified = isUnifiedDataVersion(dataVersion);
  const frames =
    isUnified && selectedRawAlarm?.media?.frames?.length
      ? selectedRawAlarm.media.frames
      : data?.media?.frames;
  const alarmVideoRef = useRef<AlarmVideoHandle>(null);
  const unauthorizedFaces = useMemo(
    () =>
      sortedRawAlarms.flatMap((ra) =>
        toUnauthorizedFaces(ra.matchedFaces, {
          cameraName: ra.sourceEntity?.name,
          videoStartTime: ra.videoStartTime,
          alarmId: ra.id,
          alarmType: locationAlarm.locationAlarmType,
        })
      ),
    [sortedRawAlarms, locationAlarm.locationAlarmType]
  );
  // A clicked detection may belong to a raw alarm other than the one on
  // screen. Holds the alarm to switch to and the offset to seek once its
  // video loads; only consumed when selectedRawAlarm actually matches it,
  // so plain next/previous navigation is unaffected.
  const [pendingSeek, setPendingSeek] = useState<{
    alarmId: string;
    offsetSec: number;
  } | null>(null);
  const pendingSeekStartTime =
    pendingSeek && selectedRawAlarm?.id === pendingSeek.alarmId
      ? pendingSeek.offsetSec
      : 0;
  const changeSelectedEvent = useCallback(
    (next: { sourceId: string; eventId: string; timestamp: number }) => {
      setPendingSeek(null);
      setSelectedEvent(next);
    },
    []
  );
  const onSeekToFace = useCallback(
    (detection: UnauthorizedFaceSeekDetection) => {
      const targetAlarm = detection.alarmId
        ? sortedRawAlarms.find((ra) => ra.id === detection.alarmId)
        : selectedRawAlarm;
      if (!targetAlarm?.videoStartTime) {
        return;
      }
      const offsetSec =
        (detection.timestamp - new Date(targetAlarm.videoStartTime).getTime()) /
        1000;

      if (targetAlarm.id === selectedEvent.eventId) {
        alarmVideoRef.current?.seek(offsetSec);
        return;
      }

      setPendingSeek({ alarmId: targetAlarm.id, offsetSec });
      setSelectedEvent({
        eventId: targetAlarm.id,
        sourceId: targetAlarm.sourceEntity.id,
        timestamp: new Date(targetAlarm.timestamp).getTime(),
      });
    },
    [sortedRawAlarms, selectedRawAlarm, selectedEvent.eventId]
  );
  const timeLineSources = useTimelineSources(
    sortedRawAlarms.length > 0 ? sortedRawAlarms : []
  );
  const [beginTime, endTime] = eventsDomain(timeLineSources);

  const cameraIds = timeLineSources.map((item) => item.id);
  const locationId = String(locationAlarm.location?.id) || '';
  const [rawAlarmsInterval, setRawAlarmsInterval] = useState<{
    startTime: number;
    endTime: number;
  }>({ startTime: beginTime, endTime: endTime });

  const isNearbyAlarmsLoaded = useMemo(() => {
    return (
      rawAlarmsInterval.startTime !== beginTime ||
      rawAlarmsInterval.endTime !== endTime
    );
  }, [
    beginTime,
    endTime,
    rawAlarmsInterval.endTime,
    rawAlarmsInterval.startTime,
  ]);

  useEffect(() => {
    // handle use case when new alarms are added to loc alarm
    // it will trigger to fetch new raw alarms as well
    if (rawAlarmsInterval.endTime < endTime) {
      setRawAlarmsInterval((prev) => ({ ...prev, endTime: endTime }));
    }
  }, [endTime, rawAlarmsInterval.endTime]);

  useEffect(() => {
    setSelectedCameraId((prev) =>
      prev !== selectedEvent.sourceId ? selectedEvent.sourceId : prev
    );
  }, [selectedEvent.sourceId]);

  const queryParams =
    sortedRawAlarms.length > 0
      ? getAlarmsSearchParams(
          cameraIds,
          rawAlarmsInterval.startTime,
          rawAlarmsInterval.endTime,
          locationId,
          /*Business logic: On initial load fetch
        only resolved alarms and if user is asking for
        neighbor alarms then only fetch pending alarms*/
          isNearbyAlarmsLoaded
            ? ['Resolved by Hakimo', 'Pending']
            : ['Resolved by Hakimo']
        )
      : '';

  const { data: allAlarmsData, isFetching } = useAlarms(
    queryParams,
    undefined,
    undefined,
    !queryParams
  );

  useEffect(() => {
    // for shared location alarm use raw alarm data from location alarm
    const allAlarmsItems = allAlarmsData?.items || [];
    let alarmsData = [];
    const locatioAlarmRawAlarms = locationAlarm.rawAlarms.map((ra) => {
      const updatedRA = { ...ra };
      updatedRA.status = 'Pending';
      return updatedRA;
    });
    if (isShared) {
      alarmsData = [...locatioAlarmRawAlarms];
    } else if (!isNearbyAlarmsLoaded) {
      /*This case is for initial load where raw alarms only fetches resolved alarms.
      Here location alarm and raw alarms api are being clubbed together*/
      alarmsData = [...locatioAlarmRawAlarms, ...allAlarmsItems];
    } else {
      /*This case is where user has asked for
      neighbour alarms and as per search query param
      it will fetch all raw alarms including pending and resolved ones */
      alarmsData = allAlarmsItems;
    }

    if (alarmsData) {
      // once all raw alarms are fetched for the given time interval, pass into events component
      const sortedAllRawAlarms = [...alarmsData].sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      const allTimelineSources = Array.from(
        sortedAllRawAlarms
          .reduce(
            createOrUpdateTimelineSource,
            new Map<string, TimelineSource>()
          )
          .values()
      );
      setAllRawAlarmTimelineSources(allTimelineSources);
    }
  }, [
    locationAlarm.rawAlarms,
    allAlarmsData?.items,
    isShared,
    isNearbyAlarmsLoaded,
  ]);

  const onNextEvent = useCallback(
    (isAutoplay?: boolean) => {
      const selectedTimelineSource = allRawAlarmTimelineSources.find(
        (item) => selectedEvent.sourceId === item.id
      );
      const timelinePendingEvents = selectedTimelineSource?.events.filter(
        (ev) => ev.status.toLowerCase() === 'pending'
      );

      const selectedEventIndex = timelinePendingEvents?.findIndex(
        (item) => item.id === selectedEvent.eventId
      );

      if (
        timelinePendingEvents &&
        selectedEventIndex !== undefined &&
        selectedTimelineSource
      ) {
        // case where next alarm is autoplayed.
        // Play only adjacent alarms for the same camera
        if (isAutoplay) {
          if (selectedEventIndex < timelinePendingEvents?.length - 1) {
            const newEvent = timelinePendingEvents[selectedEventIndex + 1];
            // adjacent alarms is when difference b/w current alarm end time
            // and next alarm start time is less than 5 seconds
            const isAdjacentAlarm =
              newEvent.timespan[0] -
                timelinePendingEvents[selectedEventIndex].timespan[1] <
              5000;
            isAdjacentAlarm &&
              changeSelectedEvent({
                eventId: newEvent.id,
                sourceId: selectedTimelineSource?.id,
                timestamp: newEvent.timespan[0],
              });
          }
          return;
        }
        // Case where next button is clicked
        if (selectedEventIndex < timelinePendingEvents?.length - 1) {
          const newEvent = timelinePendingEvents[selectedEventIndex + 1];
          changeSelectedEvent({
            eventId: newEvent.id,
            sourceId: selectedTimelineSource?.id,
            timestamp: newEvent.timespan[0],
          });
        } else {
          // this case suggests moving to next timeline source
          const timelineSourceIndex = allRawAlarmTimelineSources.findIndex(
            (item) => selectedEvent.sourceId === item.id
          );
          if (timelineSourceIndex < allRawAlarmTimelineSources.length - 1) {
            const nextTimeline =
              allRawAlarmTimelineSources[timelineSourceIndex + 1];
            const nextTimelinePendingEvents = nextTimeline?.events.filter(
              (ev) => ev.status.toLowerCase() === 'pending'
            );
            changeSelectedEvent({
              sourceId: nextTimeline.id,
              eventId: nextTimelinePendingEvents[0].id,
              timestamp: nextTimeline.events[0].timespan[0],
            });
          }
        }
      }
    },
    [
      allRawAlarmTimelineSources,
      changeSelectedEvent,
      selectedEvent.eventId,
      selectedEvent.sourceId,
    ]
  );

  const onPreviousEvent = () => {
    const selectedTimelineSource = allRawAlarmTimelineSources.find(
      (item) => selectedEvent.sourceId === item.id
    );
    const timelinePendingEvents = selectedTimelineSource?.events.filter(
      (ev) => ev.status.toLowerCase() === 'pending'
    );
    const selectedEventIndex = timelinePendingEvents?.findIndex(
      (item) => item.id === selectedEvent.eventId
    );
    if (
      timelinePendingEvents &&
      selectedEventIndex !== undefined &&
      selectedTimelineSource
    ) {
      if (selectedEventIndex > 0) {
        const newEvent = timelinePendingEvents[selectedEventIndex - 1];
        changeSelectedEvent({
          eventId: newEvent.id,
          sourceId: selectedTimelineSource?.id,
          timestamp: newEvent.timespan[0],
        });
      } else {
        // this case suggests moving to previous timeline source
        const timelineSourceIndex = allRawAlarmTimelineSources.findIndex(
          (item) => selectedEvent.sourceId === item.id
        );
        if (timelineSourceIndex > 0) {
          const prevTimeline =
            allRawAlarmTimelineSources[timelineSourceIndex - 1];
          const prevTimelinePendingEvents = prevTimeline?.events.filter(
            (ev) => ev.status.toLowerCase() === 'pending'
          );
          changeSelectedEvent({
            sourceId: prevTimeline.id,
            eventId: prevTimelinePendingEvents[0].id,
            timestamp: prevTimeline.events[0].timespan[0],
          });
        }
      }
    }
  };
  const onStatusChangeCb = (status: StatusType) => {
    onTrackResolve();
    onStatusChange && onStatusChange(status);
  };
  const onLoadNearbyAlarms = (seconds: number) => {
    const timeToChange = seconds < 0 ? 'startTime' : 'endTime';
    const dateObj = new Date(rawAlarmsInterval[timeToChange]);
    dateObj.setSeconds(dateObj.getSeconds() + seconds);
    const updatedTime = dateObj.getTime();
    setRawAlarmsInterval((prev) => ({ ...prev, [timeToChange]: updatedTime }));
  };

  const showExtraActions = useMemo(() => {
    return !isShared && (allAlarmsData?.items?.length ?? 0) > 0;
  }, [isShared, allAlarmsData?.items]);

  const onSelectLiveCameraFeed = (camId: string) => setSelectedCameraId(camId);
  const cameraNames = [
    ...new Set(
      sortedRawAlarms
        .map((a) => a.sourceEntity.name)
        .filter((name): name is string => !!name)
    ),
  ];
  return (
    <div
      className={clsx(
        'bg-hakimo-light-bg dark:bg-hakimo-dark-bg sticky top-0 flex h-full w-full flex-col rounded-lg',
        layout === 'list' && 'ml-2',
        layout === 'details' && 'ml-0 pl-0'
      )}
    >
      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 mb-1 rounded-lg py-1">
        {!isShared && (
          <Header
            alarm={locationAlarm}
            viewMode={viewMode}
            onChangeViewMode={setViewMode}
            onStatusChange={onStatusChangeCb}
            showResolveAction={showResolveAction}
            cameraNames={cameraNames}
            aiOperatorRep={aiOperatorRep}
          />
        )}
      </div>
      <div className="space-y-4 pb-4 pt-1">
        <div
          className={clsx(
            'flex flex-col gap-2',
            viewMode === 'split' && !isShared && 'xl:grid xl:grid-cols-2'
          )}
        >
          <div
            className="order-0 flex-1 space-y-3 xl:order-none"
            hidden={viewMode === 'live' && !isShared}
          >
            <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-2">
              {/* Scan-style media for frames alarms: v3 delivers scan-shaped
              frames (no videoPath), so render just the frames player with
              matched faces on — exactly how a scan alarm shows it. The RG
              event-time header and video next/prev nav are RG-video concepts, so
              they stay in the video branch. */}
              {frames?.length ? (
                <AlarmFrames
                  key={isUnified ? selectedEvent.eventId : undefined}
                  frames={frames}
                  timeZone={locationAlarm.location?.timezone ?? 'UTC'}
                  showMatchedFaces
                  disableDownload={disableMediaDownload}
                />
              ) : (
                <>
                  <AlarmHeader beginTime={beginTime} endTime={endTime} />
                  <AlarmVideo
                    videoPath={data?.videoPath}
                    startTime={pendingSeekStartTime}
                    boxes={data?.boxes}
                    matchedFaces={selectedRawAlarm?.matchedFaces}
                    cameraName={selectedRawAlarm?.sourceEntity?.name}
                    videoStartTime={selectedRawAlarm?.videoStartTime}
                    alarmType={locationAlarm.locationAlarmType}
                    disableDownload={disableMediaDownload}
                    ref={alarmVideoRef}
                    onNext={onNextEvent}
                    onPrevious={onPreviousEvent}
                    isAutoplayEnabled={!!isAutoplayEnabled}
                    hasPrevious={
                      sortedRawAlarms.findIndex(
                        (a) => a.id === selectedEvent.eventId
                      ) > 0
                    }
                    hasNext={
                      sortedRawAlarms.findIndex(
                        (a) => a.id === selectedEvent.eventId
                      ) <
                      sortedRawAlarms.length - 1
                    }
                  />
                </>
              )}
            </div>
          </div>

          {!isShared && (
            <div
              className="order-2 flex-1 space-y-4 xl:order-none"
              hidden={viewMode === 'events'}
            >
              {selectedCameraId && (
                <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-2">
                  <LiveView
                    alarms={sortedRawAlarms}
                    locationAlarmId={locationAlarm.id}
                    selectedCameraId={selectedCameraId}
                    onChangeSelectedCameraId={setSelectedCameraId}
                    onTalkdown={onTrackTalkdown}
                    location={locationAlarm.location}
                  />
                </div>
              )}
            </div>
          )}

          <div
            className="order-1 col-span-2 xl:order-none"
            hidden={viewMode === 'live' && !isShared}
          >
            <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-2">
              {allRawAlarmTimelineSources.length > 0 ? (
                <EventTimeline
                  sources={allRawAlarmTimelineSources}
                  selected={selectedEvent.eventId}
                  liveSourceId={selectedCameraId}
                  isFetchingAlarms={isFetching}
                  onChangeSelected={(eventId, sourceId, timestamp) =>
                    changeSelectedEvent({ eventId, sourceId, timestamp })
                  }
                  onLoadNearbyAlarms={onLoadNearbyAlarms}
                  rawAlarmsInterval={rawAlarmsInterval}
                  showExtraActions={showExtraActions}
                  isSharedAlarm={isShared}
                  isAutoplayEnabled={!!isAutoplayEnabled}
                  onChangeAutoplayEnabled={(val: boolean) =>
                    setIsAutoplayEnabled(val)
                  }
                  onSelectCamera={onSelectLiveCameraFeed}
                  dataVersion={dataVersion}
                />
              ) : (
                <div className="flex justify-center">
                  <HakimoSpinner />
                </div>
              )}
            </div>
          </div>
          {!isShared && (
            <div className="order-3 xl:col-span-2 xl:block">
              <div className="flex flex-col gap-2 xl:flex-row">
                <div className="xl:flex xl:w-1/2 xl:flex-col xl:gap-2">
                  <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-2">
                    <AIRecommendationWrapper
                      alarmId={locationAlarm.id}
                      timeZone={locationAlarm.location.timezone}
                    />
                  </div>
                  {unauthorizedFaces.length > 0 && (
                    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-2">
                      <UnauthorizedFaceDetection
                        faces={unauthorizedFaces}
                        timeZone={locationAlarm.location.timezone}
                        onSeek={onSeekToFace}
                      />
                    </div>
                  )}
                  <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-2">
                    <LocationFloorplan
                      locationId={String(locationAlarm.location.id)}
                    />
                  </div>
                  {showContacts && (
                    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-2">
                      <LocationContactsWrapper
                        locationId={locationAlarm.location.id}
                        locationAlarmId={locationAlarm.id}
                        locationTenantId={locationAlarm.location.tenant_id}
                      />
                    </div>
                  )}
                  <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-2">
                    <AlarmUpdates
                      locationAlarmId={locationAlarm.id}
                      locationName={locationAlarm.location.name}
                      timestamp={locationAlarm.alarmTime}
                      dataVersion={dataVersion}
                    />
                  </div>
                </div>
                <div className="xl:flex xl:w-1/2 xl:flex-col xl:gap-2">
                  {showSOP && (
                    <div>
                      <SOP
                        locationId={String(locationAlarm.location.id)}
                        tenantId={locationAlarm.location.tenant_id}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
export default memo(
  withAuthz(AlarmOverview, ['location_alarm/detail:view']),
  arePropsEqual
);
export const AlarmOverviewNoAuth = memo(AlarmOverview, arePropsEqual);
